import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template53: Página de Akihabara de Tokio
// Copia DIRECTA del componente PageAkihabara.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template53 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [akihabaraImageError, setAkihabaraImageError] = useState(false);
	const [retroGamesImageError, setRetroGamesImageError] = useState(false);
	const [gigoImageError, setGigoImageError] = useState(false);
	const [badgeAkihabaraError, setBadgeAkihabaraError] = useState(false);
	const [cosplayImageError, setCosplayImageError] = useState(false);

	// Datos hardcodeados de Akihabara (valores por defecto)
	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const akihabaraImage = getImageSrc("akihabara-main");
	const retroGamesImage = getImageSrc("retro-games");
	const gigoImage = getImageSrc("gigo");
	const cosplayImage = getImageSrc("cosplay");
	const badgeAkihabara = getCanvasImageUrl("badge-akihabara");

	// Textos desde page.texts
	const akihabaraTitle = page.texts?.[1]?.content;
	const sabiasQueText = page.texts?.[2]?.content;

	// Mapa desde page.maps
	const mapUrl = page.maps?.[0];

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	// Parsear el texto de SabiasQue para extraer los iconos y textos
	const parseSabiasQue = (htmlText) => {
		if (!htmlText) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlText, "text/html");
		const paragraphs = doc.querySelectorAll("p");
		const items = [];
		paragraphs.forEach((p) => {
			const text = p.textContent ?? p.innerText;
			const emojiMatch = text.match(/^([^\s]+)\s/);
			if (emojiMatch) {
				items.push({
					icono: emojiMatch[1],
					texto: text.substring(emojiMatch[1].length + 1).trim(),
				});
			}
		});
		return items;
	};

	const sabiasQueItems = parseSabiasQue(sabiasQueText);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	if (fullscreen) {
		// Vista de mapa grande
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#24221a",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa grande akihabara"
						src={mapUrl}
						width="100%"
						height="100%"
						sx={{
							border: 0,
							flex: 1,
						}}
						allowFullScreen
						loading="lazy"
					/>
				) : (
					<Box
						sx={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SinConexionKawaii height="400px" fontFamily={fontFamily} />
					</Box>
				)}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header]):not([data-map-button])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{!isSmallScreen && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA */}
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							color: "black",
							fontFamily: `${fontFamily}`,
							fontSize: 14,
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #24221a",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{isOnline && (
					<Box
						component="button"
						data-map-button
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: "80px",
							right: "10px",
							backgroundColor: "#24221a",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa Akihabara"
						src={mapUrl}
						width="100%"
						height="400px"
						allowFullScreen=""
						loading="lazy"
						sx={{
							border: 0,
						}}
						onLoad={() => setLoaded(true)}
					/>
				) : !isOnline ? (
					<SinConexionKawaii height="400px" fontFamily={fontFamily} />
				) : null}

				{badgeAkihabara && !badgeAkihabaraError && (
					<img
						src={badgeAkihabara}
						alt=""
						onError={() => setBadgeAkihabaraError(true)}
						style={{
							width: 140,
							position: "absolute",
							bottom: "-10%",
							right: -80,
						}}
					/>
				)}

				{cosplayImage && !cosplayImageError && (
					<img
						src={cosplayImage}
						alt=""
						onError={() => setCosplayImageError(true)}
						style={{
							width: 120,
							position: "absolute",
							bottom: -10,
							left: -20,
						}}
					/>
				)}
				<Box
					sx={{
						position: "absolute",
						bottom: isSmallScreen ? "70px" : "75px",
						left: "18px",
						backgroundColor: "#fff8db",
						border: "2px solid #f5c400",
						borderRadius: "16px 16px 16px 0px",
						padding: "10px 12px",
						fontSize: 11,
						fontFamily: `${fontFamily} !important`,
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: isSmallScreen ? 120 : 160,
					}}
				>
					{isSmallScreen ? (
						<>{page?.texts?.[3]?.content}</>
					) : (
						<>
							<Box component="strong">{page?.texts?.[4]?.content}</Box>{" "}
							{page?.texts?.[3]?.content}
						</>
					)}
				</Box>
			</Box>

			{/* CONTENIDO */}
			<Box
				sx={{
					padding: "10px 12px",
					flex: 1,
					position: "relative",
				}}
			>
				<Box data-block-id="text-1" sx={{ position: "relative" }}>
				<Box
					component="h3"
					sx={{
						fontSize: 14,
						fontWeight: 700,
						fontFamily: `${fontFamily} !important`,
						textTransform: "uppercase",
						marginTop: 0.25,
						marginBottom: "8px",
					}}
				>
					{akihabaraTitle}
				</Box>
				{sabiasQueItems.length > 0 &&
					[
						["32%", 0],
						["42%", 1],
						["52%", 2],
						["62%", 3],
					].map(([left, idx]) =>
						sabiasQueItems[idx] ? (
							<Box
								key={idx}
								sx={{
									position: "absolute",
									top: 0,
									left,
								}}
							>
								<SabiasQue
									icono={sabiasQueItems[idx].icono}
									texto={sabiasQueItems[idx].texto}
								/>
							</Box>
						) : null,
					)}
				<Box
					component="p"
					sx={{
						fontSize: 13,
						fontFamily: `${fontFamily} !important`,
						color: contentColor,
					}}
				>
					{page?.texts?.[5]?.content}
				</Box>
				</Box>

				<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px" }}>
					{akihabaraImage && !akihabaraImageError ? (
						<img
							src={akihabaraImage}
							alt=""
							onError={() => setAkihabaraImageError(true)}
							style={{
								width: "40%",
								height: 110,
								objectFit: "cover",
								borderRadius: "12px",
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width="40%"
							height={110}
							fontFamily={fontFamily}
							message="Imagen no disponible"
							compact
						/>
					)}
					<Box sx={{ width: "60%" }}>
						<Box
							component="p"
							sx={estiloP}
							dangerouslySetInnerHTML={{
								__html: page?.texts?.[6]?.content,
							}}
						></Box>
					</Box>
				</Box>
				<Box
					data-block-id="text-3"
					sx={{
						display: "flex",
						gap: "16px",
						marginTop: "20px",
						justifyContent: "flex-end",
						width: "100%",
						position: "relative",
					}}
				>
					{gigoImage && !gigoImageError ? (
						<img
							src={gigoImage}
							alt=""
							onError={() => setGigoImageError(true)}
							style={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? "auto" : 0,
								left: isSmallScreen ? -40 : -10,
								top: isSmallScreen ? -95 : "auto",
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width={110}
							height={110}
							fontFamily={fontFamily}
							message="Imagen no disponible"
							compact
						/>
					)}
					{!isSmallScreen && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "auto" : "75px",
								top: isSmallScreen ? -130 : "auto",
								left: isSmallScreen ? 0 : "18px",
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: `${fontFamily} !important`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: isSmallScreen ? 90 : 160,
							}}
							dangerouslySetInnerHTML={{
								__html: page?.texts?.[7]?.content,
							}}
						/>
					)}

					<Box>
						{retroGamesImage && !retroGamesImageError ? (
							<img
								src={retroGamesImage}
								alt=""
								onError={() => setRetroGamesImageError(true)}
								style={{
									width: isSmallScreen ? 85 : 100,
									position: "absolute",
									zIndex: 99,
									right: isSmallScreen ? -25 : 0,
									top: isSmallScreen ? -45 : -70,
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width={isSmallScreen ? 85 : 100}
								height={80}
								fontFamily={fontFamily}
								message="Imagen no disponible"
								compact
							/>
						)}
					</Box>

					<Box
						sx={{
							backgroundColor: "#ffe3cc",
							border: "3px solid #f28b35",
							borderRadius: "12px",
							padding: "8px 20px",
							fontSize: 12,
							fontFamily: `${fontFamily} !important`,
							color: "#333",
							width: isSmallScreen ? "100%" : "70%",
						}}
					>
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: 13,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								gap: 6,
								color: "#f28b35",
								textTransform: "uppercase",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{page?.texts?.[8]?.content}
						</Box>
						<Box
							component="div"
							dangerouslySetInnerHTML={{
								__html: page?.texts?.[9]?.content,
							}}
							sx={{
								margin: 0,
								fontFamily: `${fontFamily} !important`,
							}}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template53.thumbnail = "/images/template-1.png";

export default Template53;
